import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiUser,
  FiClipboard,
  FiBarChart2,
  FiPlus,
  FiCheck,
  FiPrinter,
  FiInbox,
  FiEdit2,
  FiX,
} from "react-icons/fi";

import EditSettlementModal from "./EditSettlementModal";
import {
  fetchRepresentatives,
  fetchSummary,
  fetchSettlements,
  createSettlement,
  updateSettlement,
} from "./settlementApi";
import { printSettlement } from "./settlementPrint";
import { money, formatDate } from "./settlementFormatters";

const RepresentativeSettlementPage = () => {
 

  const [representatives, setRepresentatives] = useState([]);
  const [loadingRepresentatives, setLoadingRepresentatives] = useState(false);



  const [representativeSearch, setRepresentativeSearch] = useState("");

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Main filter panel

  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  const [activeSettlementTab, setActiveSettlementTab] = useState("current");
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);

  // Edit (PUT)
  const [editingSettlement, setEditingSettlement] = useState(null);
  const [updatingSettlement, setUpdatingSettlement] = useState(false);
  const [filters, setFilters] = useState({
  RepresentativeId: "",
  Date: new Date().toISOString().split("T")[0],

  ShippingPerOrder: "",
  ShippingAmount: "",

  Code: "",
  Codes: [],

  Customer: "",
  Phone: "",
  Address: "",

  Governorate: "",
  Governorates: [],
  GovernorateId: "",

  Status: "",
  Statuses: [],

  CategoryName: "",
  CategoryId: "",

  MinTotal: "",
  MaxTotal: "",

  represntativeId: "",
  dateDaily: "",
});

  const [settlementForm, setSettlementForm] = useState({
    shippingPerOrder: "",
    cashAmount: "",
    instaPayAmount: "",
    vodafoneCashAmount: "",
    paidAmount: "",
    notes: "",
  });

  // =========================================================
  // Get Representatives
  // =========================================================

  const getRepresentatives = async () => {
    try {
      setLoadingRepresentatives(true);

      const data = await fetchRepresentatives();
      setRepresentatives(data);
    } catch (error) {
      console.error("GET REPRESENTATIVES ERROR:", error);
      Swal.fire({ icon: "error", title: "خطأ", text: "حدث خطأ أثناء جلب المندوبين", confirmButtonText: "حسناً" });
    } finally {
      setLoadingRepresentatives(false);
    }
  };

  // =========================================================
  // Initial Load
  // =========================================================

  useEffect(() => {
    getRepresentatives();
  }, []);

  // =========================================================
  // Selected Representative
  // =========================================================

  const representativeId = filters.RepresentativeId;
  const selectedDate = filters.Date;

  const selectedRepresentative = useMemo(() => {
    return representatives.find((rep) => Number(rep.id) === Number(representativeId));
  }, [representatives, representativeId]);

  // =========================================================
  // Get Summary
  // =========================================================
const handleFilterChange = (name, value) => {
  setFilters((prev) => ({
    ...prev,
    [name]: value,
  }));

  setSummary(null);
  setSettlements([]);
  setShowSettlementForm(false);
  setActiveSettlementTab("current");
};

  const getSummary = async () => {
  try {
    setLoadingSummary(true);
    setSummary(null);
    setSettlements([]);
    setShowSettlementForm(false);

    const data = await fetchSummary(filters);

    setSummary(data);

    // سجل التسويات
    if (filters.RepresentativeId && filters.Date) {
      await getSettlements(
        filters.RepresentativeId,
        filters.Date
      );
    }
  } catch (error) {
    console.error("GET SUMMARY ERROR:", error);

    Swal.fire({
      icon: "error",
      title: "خطأ",
      text:
        error?.response?.data?.message ||
        "حدث خطأ أثناء جلب بيانات التصفية",
      confirmButtonText: "حسناً",
    });
  } finally {
    setLoadingSummary(false);
  }
};

  // =========================================================
  // Get Settlement Records
  // =========================================================

  const getSettlements = async (repId, date) => {
    if (!repId || !date) return;

    try {
      setLoadingSettlements(true);

      const data = await fetchSettlements(repId, date);
      setSettlements(data);
    } catch (error) {
      console.error("GET SETTLEMENTS ERROR:", error);
      setSettlements([]);
    } finally {
      setLoadingSettlements(false);
    }
  };

  // =========================================================
  // Date Change
  // =========================================================

const handleDateChange = (e) => {
  handleFilterChange("Date", e.target.value);
};

  // =========================================================
  // Settlement Form Change
  // =========================================================

  const handleSettlementChange = (e) => {
    const { name, value } = e.target;
    setSettlementForm((prev) => ({ ...prev, [name]: value }));
  };

  // =========================================================
  // Create Settlement
  // =========================================================

  const handleCreateSettlement = async (e) => {
    e.preventDefault();

    if (!selectedRepresentative) {
      Swal.fire({ icon: "warning", title: "اختر المندوب", text: "من فضلك اختر المندوب", confirmButtonText: "حسناً" });
      return;
    }

    if (!summary) {
      Swal.fire({ icon: "warning", title: "لا توجد بيانات", text: "اعرض بيانات المندوب أولاً", confirmButtonText: "حسناً" });
      return;
    }

    try {
      setSavingSettlement(true);

      /*
       * مهم:
       * مش بنبعت shippingAmount
       * ومش بنبعت paidAmount كـ total منفصل.
       *
       * الـ API هو اللي بيحسب القيم التلقائية.
       */

      const payload = {
        representativeId: Number(selectedRepresentative.id),
        settlementDate: `${selectedDate}T00:00:00`,
        shippingPerOrder: Number(settlementForm.shippingPerOrder) || 0,
        cashAmount: Number(settlementForm.cashAmount) || 0,
        instaPayAmount: Number(settlementForm.instaPayAmount) || 0,
        vodafoneCashAmount: Number(settlementForm.vodafoneCashAmount) || 0,
        notes: settlementForm.notes || "",
      };

      await createSettlement(payload);

      Swal.fire({ icon: "success", title: "تمت التسوية", text: "تم حفظ التسوية بنجاح", confirmButtonText: "حسناً" });

      setShowSettlementForm(false);
      setSettlementForm({
        shippingPerOrder: "",
        cashAmount: "",
        instaPayAmount: "",
        vodafoneCashAmount: "",
        paidAmount: "",
        notes: "",
      });

      // تحديث الـ summary
      await getSummary();

      // تحديث سجل التسويات
      await getSettlements(selectedRepresentative.id, selectedDate);
    } catch (error) {
      console.error("CREATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء حفظ التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSavingSettlement(false);
    }
  };

  // =========================================================
  // Update Settlement (PUT)
  // =========================================================

  const handleUpdateSettlement = async (form) => {
    if (!editingSettlement) return;

    try {
      setUpdatingSettlement(true);

      const cashAmount = Number(form.cashAmount) || 0;
      const instaPayAmount = Number(form.instaPayAmount) || 0;
      const vodafoneCashAmount = Number(form.vodafoneCashAmount) || 0;

      const payload = {
        cashAmount,
        instaPayAmount,
        vodafoneCashAmount,
        paidAmount: cashAmount + instaPayAmount + vodafoneCashAmount,
        shippingPerOrder: Number(form.shippingPerOrder) || 0,
        shippingAmount: Number(editingSettlement.shippingAmount) || 0,
        notes: form.notes || "",
      };

      await updateSettlement(editingSettlement.id, payload);

      Swal.fire({ icon: "success", title: "تم التعديل", text: "تم تعديل التسوية بنجاح", confirmButtonText: "حسناً" });

      setEditingSettlement(null);

      // تحديث الـ summary وسجل التسويات
      await getSummary();
    } catch (error) {
      console.error("UPDATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء تعديل التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setUpdatingSettlement(false);
    }
  };

  // =========================================================
  // Print Settlement
  // =========================================================

  const handlePrintSettlement = (item) => {
    const opened = printSettlement(item);

    if (!opened) {
      Swal.fire({
        icon: "warning",
        title: "تعذر فتح نافذة الطباعة",
        text: "من فضلك اسمح بالنوافذ المنبثقة (popups) لهذا الموقع وحاول مرة أخرى",
        confirmButtonText: "حسناً",
      });
    }
  };

  // =========================================================
  // Helpers
  // =========================================================

  /*
   * المتبقي:
   *
   * موجب  => المندوب عليه فلوس
   * صفر   => تم التقفيل
   * سالب  => الشركة عليها فلوس للمندوب
   */

 const getStatusClass = (status) => {
  const value = String(status || "").trim();

  if (value === "تم التسليم" || value === "تم التوصيل" || value === "Delivered") {
    return "bg-green-100 text-green-700";
  }

  if (value === "مرتجع" || value === "Returned") {
    return "bg-red-100 text-red-700";
  }

  if (value === "معلق" || value === "معلّق" || value === "Pending") {
    return "bg-yellow-100 text-yellow-700";
  }

  return "bg-slate-100 text-slate-700";
 };

 const renderRemaining = (remaining) => {
  const value = Number(remaining || 0);

  if (value < 0) {
    return (
      <div>
        <span className="font-black text-purple-700">للمندوب</span>
        <div className="font-bold text-purple-700">
          {money(Math.abs(value))} ج.م
        </div>
      </div>
    );
  }

  if (value === 0) {
    return (
      <div className="rounded-xl bg-green-50 p-4">
        <p className="text-sm text-green-700">الحالة</p>
        <p className="mt-2 text-xl font-black text-green-700">
          تم التقفيل
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-red-50 p-4">
      <p className="text-sm text-red-700">المتبقي على المندوب</p>
      <p className="mt-2 text-xl font-black text-red-700">
        {money(value)} ج.م
      </p>
    </div>
  );
};


  return (
    <>
          {/* COMPACT FILTERS - SAME DESIGN AS PAYMENT METHODS */}
          <section className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-800">فلاتر التصفية</h2>
                <p className="mt-1 text-sm text-slate-500">
                  استخدم أي عدد من الفلاتر ثم اضغط بحث لعرض التصفية
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[2500px] text-right text-sm">
                <thead>
                  {/* FILTER ROW */}
                  <tr className="border-b border-slate-200 bg-white">
                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={representativeSearch}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRepresentativeSearch(value);

                          const matched = representatives.find(
                            (rep) =>
                              String(rep?.fullName || "").trim().toLowerCase() ===
                              value.trim().toLowerCase()
                          );

                          handleFilterChange(
                            "RepresentativeId",
                            matched ? String(matched.id) : ""
                          );
                        }}
                        list="settlement-representatives"
                        placeholder="المندوب"
                        className="h-8 w-full min-w-[170px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                      <datalist id="settlement-representatives">
                        {representatives.map((rep) => (
                          <option key={rep.id} value={rep.fullName || ""} />
                        ))}
                      </datalist>
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="date"
                        value={filters.Date}
                        onChange={(e) => handleFilterChange("Date", e.target.value)}
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="datetime-local"
                        value={filters.dateDaily}
                        onChange={(e) => handleFilterChange("dateDaily", e.target.value)}
                        className="h-8 w-full min-w-[155px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Code}
                        onChange={(e) => handleFilterChange("Code", e.target.value)}
                        placeholder="كود الأوردر"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Codes.join(", ")}
                        onChange={(e) =>
                          handleFilterChange(
                            "Codes",
                            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
                          )
                        }
                        placeholder="أكواد متعددة"
                        className="h-8 w-full min-w-[145px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Customer}
                        onChange={(e) => handleFilterChange("Customer", e.target.value)}
                        placeholder="العميل"
                        className="h-8 w-full min-w-[130px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Phone}
                        onChange={(e) => handleFilterChange("Phone", e.target.value)}
                        placeholder="الهاتف"
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Address}
                        onChange={(e) => handleFilterChange("Address", e.target.value)}
                        placeholder="العنوان"
                        className="h-8 w-full min-w-[150px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Governorate}
                        onChange={(e) => handleFilterChange("Governorate", e.target.value)}
                        placeholder="المحافظة"
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={filters.GovernorateId}
                        onChange={(e) => handleFilterChange("GovernorateId", e.target.value)}
                        placeholder="رقم المحافظة"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Governorates.join(", ")}
                        onChange={(e) =>
                          handleFilterChange(
                            "Governorates",
                            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
                          )
                        }
                        placeholder="محافظات متعددة"
                        className="h-8 w-full min-w-[150px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Status}
                        onChange={(e) => handleFilterChange("Status", e.target.value)}
                        placeholder="الحالة"
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.Statuses.join(", ")}
                        onChange={(e) =>
                          handleFilterChange(
                            "Statuses",
                            e.target.value.split(",").map((item) => item.trim()).filter(Boolean)
                          )
                        }
                        placeholder="حالات متعددة"
                        className="h-8 w-full min-w-[150px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="text"
                        value={filters.CategoryName}
                        onChange={(e) => handleFilterChange("CategoryName", e.target.value)}
                        placeholder="اسم التصنيف"
                        className="h-8 w-full min-w-[135px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        value={filters.CategoryId}
                        onChange={(e) => handleFilterChange("CategoryId", e.target.value)}
                        placeholder="رقم التصنيف"
                        className="h-8 w-full min-w-[120px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        step="0.01"
                        value={filters.ShippingPerOrder}
                        onChange={(e) => handleFilterChange("ShippingPerOrder", e.target.value)}
                        placeholder="الشحن / أوردر"
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        step="0.01"
                        value={filters.ShippingAmount}
                        onChange={(e) => handleFilterChange("ShippingAmount", e.target.value)}
                        placeholder="إجمالي الشحن"
                        className="h-8 w-full min-w-[125px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        step="0.01"
                        value={filters.MinTotal}
                        onChange={(e) => handleFilterChange("MinTotal", e.target.value)}
                        placeholder="أقل إجمالي"
                        className="h-8 w-full min-w-[110px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                    <th className="p-1 text-center">
                      <input
                        type="number"
                        step="0.01"
                        value={filters.MaxTotal}
                        onChange={(e) => handleFilterChange("MaxTotal", e.target.value)}
                        placeholder="أكبر إجمالي"
                        className="h-8 w-full min-w-[110px] rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                      />
                    </th>

                   
                  </tr>
           
                  {/* FILTER HEADERS */}
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {[
                      "المندوب",
                      "التاريخ",
                      "التاريخ اليومي",
                      "كود الأوردر",
                      "أكواد متعددة",
                      "العميل",
                      "الهاتف",
                      "العنوان",
                      "المحافظة",
                      "رقم المحافظة",
                      "محافظات متعددة",
                      "الحالة",
                      "حالات متعددة",
                      "اسم التصنيف",
                      "رقم التصنيف",
                      "الشحن / أوردر",
                      "إجمالي الشحن",
                      "أقل إجمالي",
                      "أكبر إجمالي",
                      // "إجراءات",
                    ].map((label) => (
                      <th
                        key={label}
                        className="p-1 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>

                   <div className="flex items-center justify-center gap-1 mt-4">
                        <button
                          type="button"
                          onClick={getSummary}
                          disabled={loadingSummary || loadingRepresentatives}
                          className="h-8 rounded-md bg-blue-600 px-3 text-xs font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {loadingSummary ? "..." : "بحث"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFilters({
                              RepresentativeId: "",
                              Date: new Date().toISOString().split("T")[0],
                              ShippingPerOrder: "",
                              ShippingAmount: "",
                              Code: "",
                              Codes: [],
                              Customer: "",
                              Phone: "",
                              Address: "",
                              Governorate: "",
                              Governorates: [],
                              GovernorateId: "",
                              Status: "",
                              Statuses: [],
                              CategoryName: "",
                              CategoryId: "",
                              MinTotal: "",
                              MaxTotal: "",
                              represntativeId: "",
                              dateDaily: "",
                            });
                            setSummary(null);
                            setSettlements([]);
                            setShowSettlementForm(false);
                            setRepresentativeSearch("");
                            setActiveSettlementTab("current");
                          }}
                          className="h-8 rounded-md border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-600 transition hover:bg-slate-50"
                          title="مسح الفلاتر"
                        >
                          مسح
                        </button>
                      </div>

                </thead>
              </table>
            </div>
          </section>

          {/* NO DATA */}
          {!summary && !loadingSummary && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-3xl text-blue-600">
                <FiUser />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-800">اختر المندوب والفلاتر المطلوبة</h3>
              <p className="mt-2 text-sm text-slate-500">ثم افتح زر "تصفية" وشغّل البحث من الفلاتر الرئيسية</p>
            </div>
          )}

          {/* LOADING */}
          {loadingSummary && (
            <div className="rounded-2xl bg-white py-16 text-center shadow-sm">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
              <p className="mt-4 text-sm text-slate-500">جاري تحميل بيانات التصفية...</p>
            </div>
          )}

          {/* SETTLEMENT SUB TABS */}
          {summary && (
              <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-2">

                  {/* التصفية الحالية */}
                  <button
                    type="button"
                    onClick={() => setActiveSettlementTab("current")}
                    className={`flex items-center justify-center gap-2 border-l border-slate-200 px-4 py-3 text-sm font-bold transition ${
                      activeSettlementTab === "current"
                        ? "bg-blue-600 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <FiClipboard className="text-sm" />
                    التصفية الحالية
                  </button>

                  {/* سجل التسويات */}
                  <button
                    type="button"
                    onClick={() => setActiveSettlementTab("history")}
                    className={`flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold transition ${
                      activeSettlementTab === "history"
                        ? "bg-blue-600 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <FiBarChart2 className="text-sm" />
                    سجل التسويات

                    {settlements.length > 0 && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                          activeSettlementTab === "history"
                            ? "bg-white/20 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {settlements.length}
                      </span>
                    )}
                  </button>

                </div>
              </div>
            )}

          {/* CURRENT SETTLEMENT TAB */}
          {summary && !loadingSummary && activeSettlementTab === "current" && (
            <>
              {/* Representative Header */}
            <section className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between px-4 py-3">

                {/* Representative */}
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                    {summary.representativeName?.charAt(0) || "م"}
                  </div>

                  <div>
                    <h2 className="text-sm font-bold text-slate-800">
                      {summary.representativeName}
                    </h2>

                    <p className="text-xs text-slate-500">
                      تاريخ التصفية: {formatDate(summary.settlementDate)}
                    </p>
                  </div>
                </div>

                {/* Action */}
                <button
                  type="button"
                  onClick={() => setShowSettlementForm(true)}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-green-700"
                >
                  <FiPlus className="text-sm" />
                  عمل تسوية
                </button>

              </div>
            </section>

             {/* STATS - SAME EXACT COMPACT STYLE AS PAYMENT METHODS */}

              <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="grid grid-cols-1 divide-y divide-slate-200 sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-5 lg:divide-x lg:divide-x-reverse">

                  {/* إجمالي الفواتير */}
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs font-medium text-slate-500">
                      إجمالي الفواتير
                    </p>

                    <p className="mt-1 text-lg font-black text-slate-800">
                      {summary.totalHandedInvoicesCount}
                    </p>

                    <p className="text-xs font-medium text-slate-500">
                      إجمالي المبلغ
                    </p>

                    <p className="text-sm font-black text-slate-800">
                      {money(summary.totalHandedAmount)} ج.م
                    </p>
                  </div>

                  {/* تم التسليم */}
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs font-medium text-slate-500">
                      تم التسليم
                    </p>

                    <p className="mt-1 text-lg font-black text-green-600">
                      {summary.deliveredInvoicesCount}
                    </p>

                    <p className="text-xs font-medium text-slate-500">
                      المبلغ
                    </p>

                    <p className="text-sm font-black text-green-700">
                      {money(summary.deliveredAmount)} ج.م
                    </p>
                  </div>

                  {/* المرتجعات */}
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs font-medium text-slate-500">
                      المرتجعات
                    </p>

                    <p className="mt-1 text-lg font-black text-red-600">
                      {summary.returnedInvoicesCount}
                    </p>

                    <p className="text-xs font-medium text-slate-500">
                      المبلغ
                    </p>

                    <p className="text-sm font-black text-red-700">
                      {money(summary.returnedAmount)} ج.م
                    </p>
                  </div>

                  {/* المعلّق */}
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs font-medium text-slate-500">
                      المعلّق
                    </p>

                    <p className="mt-1 text-lg font-black text-yellow-600">
                      {summary.pendingInvoicesCount}
                    </p>

                    <p className="text-xs font-medium text-slate-500">
                      المبلغ
                    </p>

                    <p className="text-sm font-black text-yellow-700">
                      {money(summary.pendingAmount)} ج.م
                    </p>
                  </div>

                  {/* صافي المطلوب */}
                  <div className="px-4 py-3 text-center">
                    <p className="text-xs font-medium text-slate-500">
                      صافي المطلوب
                    </p>

                    <p className="mt-1 text-lg font-black text-blue-600">
                      {money(summary.netRequiredAmount)} ج.م
                    </p>

                    <p className="text-xs font-medium text-slate-500">
                      الحالة
                    </p>

                    <p
                      className={`text-sm font-black ${
                        Number(summary.remainingAmount || 0) < 0
                          ? "text-purple-700"
                          : Number(summary.remainingAmount || 0) === 0
                          ? "text-green-700"
                          : "text-red-700"
                      }`}
                    >
                      {Number(summary.remainingAmount || 0) < 0
                        ? "للمندوب"
                        : Number(summary.remainingAmount || 0) === 0
                        ? "تم التقفيل"
                        : "على المندوب"}
                    </p>
                  </div>

                </div>
              </section>



              {/* Settlement Modal - SAME COMPACT DESIGN AS EXPENSE FORM */}
              {showSettlementForm && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[2px]" dir="rtl">
                  <div
                    className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="settlement-modal-title"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
                      <div>
                        <h2 id="settlement-modal-title" className="text-lg font-bold text-slate-800">
                          عمل تسوية
                        </h2>
                        <p className="mt-1 text-xs text-slate-500">
                          أدخل مبالغ الدفع فقط وسيتم حساب الإجماليات تلقائيًا
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowSettlementForm(false)}
                        disabled={savingSettlement}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                        title="إغلاق"
                      >
                        <FiX size={17} />
                      </button>
                    </div>

                    <form onSubmit={handleCreateSettlement} className="p-5">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-xs font-bold text-slate-700">الشحن لكل أوردر</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="shippingPerOrder"
                            value={settlementForm.shippingPerOrder}
                            onChange={handleSettlementChange}
                            placeholder="0"
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>

                        <div>
                          <label className="mb-1.5 block text-xs font-bold text-slate-700">كاش</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="cashAmount"
                            value={settlementForm.cashAmount}
                            onChange={handleSettlementChange}
                            placeholder="0"
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>

                        <div>
                          <label className="mb-1.5 block text-xs font-bold text-slate-700">InstaPay</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="instaPayAmount"
                            value={settlementForm.instaPayAmount}
                            onChange={handleSettlementChange}
                            placeholder="0"
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>

                        <div>
                          <label className="mb-1.5 block text-xs font-bold text-slate-700">Vodafone Cash</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="vodafoneCashAmount"
                            value={settlementForm.vodafoneCashAmount}
                            onChange={handleSettlementChange}
                            placeholder="0"
                            className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="mb-1.5 block text-xs font-bold text-slate-700">ملاحظات</label>
                          <textarea
                            name="notes"
                            rows="3"
                            value={settlementForm.notes}
                            onChange={handleSettlementChange}
                            placeholder="اكتب ملاحظات التسوية..."
                            className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          />
                        </div>
                      </div>

                      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4">
                        <p className="text-xs font-bold text-blue-700">صافي المطلوب الحالي</p>
                        <p className="mt-1 text-2xl font-black text-blue-800">
                          {money(summary.netRequiredAmount)} ج.م
                        </p>
                        <p className="mt-1 text-[11px] text-blue-600">
                          إجمالي الشحن وإجمالي المدفوع يتم حسابهما تلقائيًا بواسطة النظام.
                        </p>
                      </div>

                      <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowSettlementForm(false)}
                          disabled={savingSettlement}
                          className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          إلغاء
                        </button>

                        <button
                          type="submit"
                          disabled={savingSettlement}
                          className="rounded-lg bg-green-600 px-6 py-2.5 text-sm font-bold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {savingSettlement ? "جاري الحفظ..." : "حفظ التسوية"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Orders */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">أوردرات المندوب</h2>
                    <p className="mt-1 text-sm text-slate-500">جميع الفواتير الخاصة بالمندوب في التاريخ المحدد</p>
                  </div>

                  <div className="rounded-xl bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700">
                    {summary.allInvoices?.length || 0} أوردر
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">رقم الفاتورة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العميل</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الهاتف</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المحافظة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العنوان</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المنتجات</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العدد</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {summary.allInvoices?.length > 0 ? (
                        summary.allInvoices.map((invoice, index) => (
                          <tr key={invoice.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-blue-600">#{invoice.invoiceCode}</td>
                            <td className="px-4 py-4 font-medium text-slate-800">{invoice.customer}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.phone}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.governorateName}</td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-500">{invoice.address}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-600">{invoice.itemsDescription}</td>
                            <td className="px-4 py-4 text-center">
                              <span className="rounded-lg bg-slate-100 px-3 py-1 font-bold">{invoice.itemsCount}</span>
                            </td>
                            <td className="px-4 py-4 font-bold">{money(invoice.totalPrice)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-orange-600">{money(invoice.shipping)} ج.م</td>
                            <td className="px-4 py-4 text-xs text-slate-500">{formatDate(invoice.dailyDate)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="px-4 py-12 text-center text-slate-500">
                            لا توجد أوردرات
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* HISTORY TAB */}
          {summary && !loadingSummary && activeSettlementTab === "history" && (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-5">
                <h2 className="text-lg font-bold text-slate-800">سجل التسويات</h2>
                <p className="mt-1 text-sm text-slate-500">جميع عمليات التسوية للمندوب في التاريخ المحدد</p>
              </div>

              {loadingSettlements ? (
                <div className="py-16 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                  <p className="mt-3 text-sm text-slate-500">جاري تحميل سجل التسويات...</p>
                </div>
              ) : settlements.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl text-slate-500">
                    <FiInbox />
                  </div>
                  <h3 className="mt-4 font-bold text-slate-700">لا توجد تسويات</h3>
                  <p className="mt-1 text-sm text-slate-500">لا يوجد سجل تسوية لهذا المندوب في هذا التاريخ</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1600px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المندوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الفواتير</th>
                        <th className="px-4 py-4 font-bold text-slate-600">إجمالي المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">تم التسليم</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المرتجع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المعلّق</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">صافي المطلوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">كاش</th>
                        <th className="px-4 py-4 font-bold text-slate-600">InstaPay</th>
                        <th className="px-4 py-4 font-bold text-slate-600">Vodafone Cash</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المدفوع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">النتيجة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">ملاحظات</th>
                        <th className="px-4 py-4 font-bold text-slate-600 text-center">إجراءات</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {settlements.map((item, index) => {
                        const remaining = Number(item.remainingAmount || 0);

                        return (
                          <tr key={item.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-slate-800">{item.representativeName}</td>
                            <td className="px-4 py-4">{formatDate(item.settlementDate)}</td>
                            <td className="px-4 py-4 font-bold">{item.totalHandedInvoicesCount}</td>
                            <td className="px-4 py-4 font-bold">{money(item.totalHandedAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-green-600">{item.deliveredInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.deliveredAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-red-600">{item.returnedInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.returnedAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-yellow-600">{item.pendingInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.pendingAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4 font-bold text-orange-600">{money(item.shippingAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.netRequiredAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-green-600">{money(item.cashAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.instaPayAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-red-600">{money(item.vodafoneCashAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="rounded-lg bg-green-50 px-3 py-1 font-bold text-green-700">
                                {money(item.paidAmount)} ج.م
                              </span>
                            </td>

                            <td className="px-4 py-4">
                              {remaining < 0 ? (
                                <div>
                                  <span className="font-black text-purple-700">للمندوب</span>
                                  <div className="font-bold text-purple-700">{money(Math.abs(remaining))} ج.م</div>
                                </div>
                              ) : remaining === 0 ? (
                                <span className="font-bold text-green-600">تم التقفيل</span>
                              ) : (
                                <div>
                                  <span className="font-black text-red-600">على المندوب</span>
                                  <div className="font-bold text-red-600">{money(remaining)} ج.م</div>
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                  item.status === "تم التقفيل"
                                    ? "bg-green-100 text-green-700"
                                    : item.status === "تقفيل جزئي"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td className="max-w-[220px] px-4 py-4 text-slate-500">{item.notes || "-"}</td>

                            <td className="px-4 py-4 text-center">
                              <div className="inline-flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingSettlement(item)}
                                  title="تعديل التسوية"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-green-400 hover:text-green-600"
                                >
                                  <FiEdit2 />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handlePrintSettlement(item)}
                                  title="طباعة PDF"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
                                >
                                  <FiPrinter />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
      </>
  );
};

export default RepresentativeSettlementPage;